import { useMemo, useState } from 'react';
import {
  Box, Card, Group, SegmentedControl, Skeleton, Stack, Table, Text, Tooltip,
} from '@mantine/core';
import { IconChartBar } from '@tabler/icons-react';
import { EmptyState } from '@components/ccms-components/utils/EmptyState';
import { BREAKDOWN_TOP_N } from '../constants';

/**
 * BreakdownBars — a ranked horizontal bar list for lookup breakdowns
 * (cases per need / zone / area / location, registrations per course / location).
 *
 * Why not a pie/donut, and why not the shared recharts BarChartWidget:
 *
 *  - There are 10+ care needs. A circular part-to-whole stops being readable past
 *    ~7 slices, and the small slices become unlabelable. A ranked bar list stays
 *    readable at any length and reads top-to-bottom like the table it replaces.
 *  - Category names here ("Marriage & Relationships", a full campus name) are long.
 *    Horizontal rows give each label a whole text line instead of a rotated stub.
 *  - `BarChartWidget`'s `orientation="horizontal"` maps to recharts `layout="horizontal"`
 *    while setting a category YAxis — recharts wants `layout="vertical"` for that, so
 *    the shared widget renders a broken chart in this orientation.
 *
 * Colour: the job of these charts is *how many*, not *which one*, so the bar fill is a
 * single hue rather than 10+ arbitrary lookup hexes — a categorical palette that size is
 * indistinguishable under colour-vision deficiency and impossible to validate across the
 * app's 35 colour schemes. The lookup's own hex still carries identity, as the swatch
 * beside the label.
 *
 * The fill is **solid at full strength for every row**. An earlier version faded opacity
 * with magnitude, which double-encoded the same variable that bar *length* already
 * carries, and on the dark theme it pushed the smallest bars to the edge of invisibility.
 * Length is the magnitude channel; the fill just has to be seen.
 */

export interface BreakdownDatum {
  id: number;
  name?: string | null;
  color?: string | null;
  /** Primary measure — drives bar length and sort order. */
  value: number;
  /** Secondary measure shown in the tooltip and table (e.g. all-time total). */
  total?: number;
}

export interface BreakdownBarsProps {
  title: string;
  subtitle?: string;
  data: BreakdownDatum[];
  loading?: boolean;
  /** Noun for the empty state and the "Other" row, e.g. "needs". */
  entityLabel: string;
  /** Column header for `value` in the table view. */
  valueLabel?: string;
  /** Column header for `total` in the table view. */
  totalLabel?: string;
  /** Rendered instead of the chart when the data source doesn't exist yet. */
  unavailableMessage?: string;
  topN?: number;
}

/**
 * Minimum bar width, as a percentage of the track. A single-case bucket must still read as a
 * bar rather than as an empty row.
 */
const MIN_BAR_PERCENT = 4;

/** Shared bar geometry, so the breakdown and workload bars stay visually identical. */
export const BAR_HEIGHT = 10;
export const BAR_RADIUS = 5;
/** Track: a filled surface, not a hairline border colour — it has to read as an empty bar. */
export const BAR_TRACK_COLOR = 'var(--mantine-color-default-border)';
export const BAR_FILL_COLOR = 'var(--mantine-primary-color-filled)';

export default function BreakdownBars({
  title,
  subtitle,
  data,
  loading = false,
  entityLabel,
  valueLabel = 'Open',
  totalLabel = 'Total',
  unavailableMessage,
  topN = BREAKDOWN_TOP_N,
}: BreakdownBarsProps) {
  const [view, setView] = useState<'chart' | 'table'>('chart');

  /** Sorted descending, with everything past `topN` folded into a single "Other" row. */
  const rows = useMemo(() => {
    const sorted = [...data].sort((a, b) => b.value - a.value);
    if (sorted.length <= topN) return sorted;

    const head = sorted.slice(0, topN);
    const tail = sorted.slice(topN);
    return [
      ...head,
      {
        id: -1,
        name: `Other (${tail.length} ${entityLabel})`,
        color: null,
        value: tail.reduce((sum, r) => sum + r.value, 0),
        total: tail.reduce((sum, r) => sum + (r.total ?? 0), 0),
      },
    ];
  }, [data, topN, entityLabel]);

  const max = useMemo(() => Math.max(1, ...rows.map((r) => r.value)), [rows]);
  const grandTotal = useMemo(() => rows.reduce((sum, r) => sum + r.value, 0), [rows]);
  const hasFold = data.length > topN;

  return (
    <Card withBorder radius="md" padding="lg" h="100%">
      <Group justify="space-between" align="flex-start" mb="md" wrap="nowrap">
        <div>
          <Text fw={600}>{title}</Text>
          {subtitle && (
            <Text size="sm" c="dimmed">
              {subtitle}
            </Text>
          )}
        </div>

        {!unavailableMessage && !loading && rows.length > 0 && (
          <SegmentedControl
            size="xs"
            value={view}
            onChange={(v) => setView(v as 'chart' | 'table')}
            data={[
              { label: 'Chart', value: 'chart' },
              { label: 'Table', value: 'table' },
            ]}
          />
        )}
      </Group>

      {unavailableMessage ? (
        <EmptyState
          icon={IconChartBar}
          title="Not available yet"
          description={unavailableMessage}
        />
      ) : loading ? (
        <Stack gap="sm">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={26} radius="sm" />
          ))}
        </Stack>
      ) : rows.length === 0 ? (
        <EmptyState variant="no-data" entityLabel={entityLabel} icon={IconChartBar} />
      ) : view === 'table' ? (
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>{valueLabel}</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>{totalLabel}</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Share</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {/* The table view is the full set — folding only applies to the chart. */}
            {[...data].sort((a, b) => b.value - a.value).map((row) => (
              <Table.Tr key={row.id}>
                <Table.Td>
                  <Group gap="xs" wrap="nowrap">
                    <Swatch color={row.color} />
                    <Text size="sm">{row.name ?? '—'}</Text>
                  </Group>
                </Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{row.value}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>{row.total ?? '—'}</Table.Td>
                <Table.Td style={{ textAlign: 'right' }}>
                  {grandTotal ? `${Math.round((row.value / grandTotal) * 100)}%` : '—'}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      ) : (
        <Stack gap="xs">
          {rows.map((row) => {
            const share = grandTotal ? Math.round((row.value / grandTotal) * 100) : 0;
            const width = Math.max(MIN_BAR_PERCENT, (row.value / max) * 100);

            return (
              <Tooltip
                key={row.id}
                withArrow
                label={
                  <Stack gap={2}>
                    <Text size="xs" fw={600}>{row.name ?? '—'}</Text>
                    <Text size="xs">{valueLabel}: {row.value} ({share}%)</Text>
                    {row.total !== undefined && (
                      <Text size="xs">{totalLabel}: {row.total}</Text>
                    )}
                  </Stack>
                }
              >
                <Box>
                  <Group gap="xs" wrap="nowrap" mb={4}>
                    <Swatch color={row.color} />
                    <Text size="sm" style={{ flex: 1, minWidth: 0 }} truncate>
                      {row.name ?? '—'}
                    </Text>
                    {/* The count wears primary ink, not dimmed — it is the value being read. */}
                    <Text size="sm" fw={700}>
                      {row.value}
                    </Text>
                  </Group>

                  {/* Track + fill. Solid fill; bar length alone carries magnitude. */}
                  <Box
                    style={{
                      height: BAR_HEIGHT,
                      borderRadius: BAR_RADIUS,
                      backgroundColor: BAR_TRACK_COLOR,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      style={{
                        height: '100%',
                        width: `${width}%`,
                        borderRadius: BAR_RADIUS,
                        backgroundColor: BAR_FILL_COLOR,
                        transition: 'width 240ms ease',
                      }}
                    />
                  </Box>
                </Box>
              </Tooltip>
            );
          })}

          {hasFold && (
            <Text size="xs" c="dimmed" mt={4}>
              Showing the top {topN} of {data.length} {entityLabel} — switch to Table for the full list.
            </Text>
          )}
        </Stack>
      )}
    </Card>
  );
}

/**
 * The lookup's own hex, carrying identity beside the magnitude bar.
 *
 * The ring matters: a 10px chip of a near-black need colour is invisible on the dark theme, and a
 * near-white one vanishes on the light theme. A 1px surface-contrast outline keeps the chip's
 * silhouette readable whatever the admin picked in `/LookUp`.
 */
function Swatch({ color }: { color?: string | null }) {
  return (
    <Box
      style={{
        width: 12,
        height: 12,
        borderRadius: 3,
        flexShrink: 0,
        backgroundColor: color || 'var(--mantine-color-dimmed)',
        boxShadow: '0 0 0 1px var(--mantine-color-default-border)',
      }}
    />
  );
}
